if (!customElements.get('jd-retailer-locator')) {
  customElements.define('jd-retailer-locator', class extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.search = this.querySelector('[data-search]');
      this.country = this.querySelector('[data-country-filter]');
      this.region = this.querySelector('[data-region]');
      this.map = this.querySelector('.jrl__map');
      this.pins = this.querySelector('.jrl__pins');
      this.tiles = this.querySelector('.jrl__tiles');
      this.cards = this.querySelector('.jrl__cards');
      this.geo = {};
      this.geoRequests = {};
      this.pageNumber = 1;
      this.pageSize = 6;
      this.center = [43, -95];
      this.zoom = 3;
      this.tileNodes = new Map();
      this.tileFailures = 0;
      this.regions = {
        US: {AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',CT:'Connecticut',DE:'Delaware',DC:'Washington, DC',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming'},
        CA: {AB:'Alberta',BC:'British Columbia',MB:'Manitoba',NB:'New Brunswick',NL:'Newfoundland and Labrador',NS:'Nova Scotia',NT:'Northwest Territories',NU:'Nunavut',ON:'Ontario',PE:'Prince Edward Island',QC:'Quebec',SK:'Saskatchewan',YT:'Yukon'},
        AU: {ACT:'Australian Capital Territory',NSW:'New South Wales',NT:'Northern Territory',QLD:'Queensland',SA:'South Australia',TAS:'Tasmania',VIC:'Victoria',WA:'Western Australia'}
      };
      try { await this.loadMoreRecords(); } catch (_) { }
      if (!this.isConnected) return;
      this.readRecords();
      const countries = [...new Set(this.records.map(r => r.country))].sort();
      countries.forEach(name => this.country.add(new Option(name, name)));
      this.country.value = '';
      this.updateRegions();
      this.querySelector('.jrl__filters').hidden = false;
      this.querySelector('.jrl__map-column').hidden = false;
      this.querySelector('.jrl__pagination').hidden = false;
      this.classList.add('is-ready');
      const listen = (el, event, fn) => el.addEventListener(event, fn, {signal:this.abort.signal});
      listen(this.querySelector('form'), 'submit', e => e.preventDefault());
      let debounce;
      listen(this.search, 'input', () => { clearTimeout(debounce); debounce = setTimeout(() => { if (this.isConnected) this.filter(); }, 180); });
      listen(this.country, 'change', () => { this.region.value = ''; this.updateRegions(); this.filter(); });
      listen(this.region, 'change', () => this.filter());
      listen(this.querySelector('[data-clear]'), 'click', () => { this.search.value = ''; this.country.value = ''; this.region.value = ''; this.updateRegions(); this.filter(); });
      listen(this.querySelector('[data-prev]'), 'click', () => { this.pageNumber--; this.renderCards(); });
      listen(this.querySelector('[data-next]'), 'click', () => { this.pageNumber++; this.renderCards(); });
      listen(this.cards, 'click', e => { const button = e.target.closest('[data-view]'); if (button) this.select(this.records.find(r => r.el === button.closest('.jrl__card'))); });
      listen(this.map, 'click', e => {
        const zoom = e.target.closest('[data-zoom]');
        if (zoom) { this.zoom = Math.max(1, Math.min(17, this.zoom + Number(zoom.dataset.zoom))); this.draw(); }
        if (e.target.closest('[data-fit]')) { this.fit(this.filtered); this.draw(); }
      });
      listen(this.querySelector('[data-explore-toggle]'), 'click', e => {
        const active = !this.map.classList.contains('is-exploring');
        this.map.classList.toggle('is-exploring', active);
        e.currentTarget.setAttribute('aria-pressed', String(active));
        e.currentTarget.textContent = active ? this.dataset.done : this.dataset.explore;
      });
      listen(this.map, 'keydown', e => {
        if (e.target !== this.map) return;
        const delta = {ArrowLeft:[-90,0],ArrowRight:[90,0],ArrowUp:[0,-90],ArrowDown:[0,90]}[e.key];
        if (delta) { e.preventDefault(); const c = this.project(this.center); this.center = this.unproject([c[0] + delta[0], c[1] + delta[1]]); this.draw(); }
        if (e.key === '+' || e.key === '=' || e.key === '-') { e.preventDefault(); this.zoom = Math.max(1,Math.min(17,this.zoom + (e.key === '-' ? -1 : 1))); this.draw(); }
      });
      let drag;
      listen(this.map, 'pointerdown', e => {
        if (e.target.closest('button,a') || e.button !== 0 || (e.pointerType !== 'mouse' && !this.map.classList.contains('is-exploring'))) return;
        drag = {id:e.pointerId,x:e.clientX,y:e.clientY,c:this.project(this.center)};
        this.map.setPointerCapture(e.pointerId);
      });
      listen(this.map, 'pointermove', e => {
        if (!drag || drag.id !== e.pointerId) return;
        this.center = this.unproject([drag.c[0] - (e.clientX - drag.x),drag.c[1] - (e.clientY - drag.y)]);
        if (!this.frame) this.frame = requestAnimationFrame(() => {this.frame = null; this.draw();});
      });
      const endDrag = () => {drag = null;};
      listen(this.map, 'pointerup', endDrag);
      listen(this.map, 'pointercancel', endDrag);
      this.resize = new ResizeObserver(() => this.draw());
      this.resize.observe(this.map);
      await this.filter();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.initialized = false;
    }
    normalize(value) { return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase().replace(/[^a-z0-9]/g,''); }
    countryCode(name) {
      const key = this.normalize(name);
      return ({canada:'CA',ca:'CA',unitedstates:'US',unitedstatesofamerica:'US',usa:'US',us:'US',australia:'AU',au:'AU'})[key] || '';
    }
    async loadMoreRecords() {
      let link = this.querySelector('.jrl__next-data');
      const seen = new Set();
      while (link && !seen.has(link.href)) {
        const url = new URL(link.href);
        if (url.origin !== location.origin) break;
        seen.add(link.href);
        const response = await fetch(url, {signal:this.abort.signal});
        if (!response.ok) break;
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const section = doc.querySelector('jd-retailer-locator');
        if (!section) break;
        section.querySelectorAll('.jrl__card').forEach(card => this.cards.append(card));
        link.remove();
        link = section.querySelector('.jrl__next-data');
        if (link) this.cards.append(link);
      }
    }
    readRecords() {
      const unique = new Set();
      this.records = Array.from(this.querySelectorAll('.jrl__card')).filter(el => {if(unique.has(el.dataset.id)){el.remove();return false;}unique.add(el.dataset.id);return true;}).map(el => {
        const r = {...el.dataset,el};
        r.code = this.countryCode(r.country);
        r.region = r.region.toUpperCase().trim();
        const found = Object.entries(this.regions[r.code] || {}).find(([,name]) => this.normalize(name) === this.normalize(r.region));
        if (found) r.region = found[0];
        const raw = r.coordinates.trim();
        const coordinates = raw.split(',').map(v => Number(v.trim()));
        if (raw && coordinates.length === 2 && coordinates.every(Number.isFinite) && Math.abs(coordinates[0]) <= 85 && Math.abs(coordinates[1]) <= 180) {r.point=coordinates;r.exact=true;}
        const pinType = el.querySelector('.jrl__pin-type');
        pinType.textContent = r.exact ? this.dataset.exactLabel : this.dataset.areaLabel;
        pinType.hidden = false;
        r.search = this.normalize([r.name,r.city,r.region,this.regions[r.code]?.[r.region],r.country,r.address].join(' '));
        return r;
      }).sort((a,b) => a.name.localeCompare(b.name) || a.city.localeCompare(b.city));
      this.records.forEach(r => this.cards.append(r.el));
    }
    updateRegions() {
      while (this.region.options.length > 1) this.region.remove(1);
      const matches = this.records.filter(r => !this.country.value || r.country === this.country.value);
      const keys = [...new Set(matches.map(r => r.region))].sort();
      keys.forEach(key => {
        const record = matches.find(r => r.region === key);
        const name = this.regions[record.code]?.[key];
        this.region.add(new Option(name ? `${name} (${key})` : key,key));
      });
    }
    async ensureGeography(records) {
      await Promise.all([...new Set(records.filter(r => !r.point).map(r => r.code).filter(Boolean))].map(async code => {
        if (!this.geoRequests[code]) this.geoRequests[code] = (async () => {
          try {
            const asset = new URL(this.dataset[code.toLowerCase()], location.href);
            let response = await fetch(asset, {signal:this.abort.signal});
            if (!response.ok && asset.search) {
              asset.search = '';
              response = await fetch(asset, {signal:this.abort.signal});
            }
            if (!response.ok) throw new Error('Geography unavailable');
            this.geo[code] = await response.json();
          } catch (_) { this.geo[code] = {}; delete this.geoRequests[code]; }
        })();
        await this.geoRequests[code];
      }));
      records.forEach(r => { if (!r.point) r.point = this.geo[r.code]?.[r.region + '|' + this.normalize(r.city)]; });
      records.forEach(r => {const button=r.el.querySelector('[data-view]');button.hidden=!r.point;});
    }
    async filter() {
      const revision = (this.filterRevision || 0) + 1;
      this.filterRevision = revision;
      const term = this.normalize(this.search.value);
      this.filtered = this.records.filter(r => (!this.country.value || r.country === this.country.value) && (!this.region.value || r.region === this.region.value) && (!term || r.search.includes(term)));
      this.selected = null;
      this.pageNumber = 1;
      this.renderCards();
      await this.ensureGeography(this.filtered);
      if (revision !== this.filterRevision || !this.isConnected) return;
      this.fit(this.filtered);
      this.draw();
    }
    renderCards() {
      const shown = new Set(this.filtered.slice((this.pageNumber-1)*this.pageSize,this.pageNumber*this.pageSize));
      this.records.forEach(r => {r.el.hidden=!shown.has(r);r.el.classList.toggle('is-selected',r === this.selected);});
      this.querySelector('.jrl__count').textContent = (this.filtered.length === 1 ? this.dataset.countOne : this.dataset.count).replace('__COUNT__',String(this.filtered.length));
      this.querySelector('.jrl__empty').hidden = this.filtered.length > 0;
      const pages = Math.max(1,Math.ceil(this.filtered.length / this.pageSize));
      this.querySelector('[data-page]').textContent = this.dataset.pageLabel.replace('__CURRENT__',String(this.pageNumber)).replace('__TOTAL__',String(pages));
      this.querySelector('[data-prev]').disabled=this.pageNumber <= 1;
      this.querySelector('[data-next]').disabled=this.pageNumber >= pages;
      this.querySelector('.jrl__pagination').hidden=pages <= 1;
      this.cards.scrollTop=0;
    }
    project(point,zoom=this.zoom) {
      const scale=256*Math.pow(2,zoom),lat=Math.max(-85,Math.min(85,point[0]))*Math.PI/180;
      return [(point[1]+180)/360*scale,(1-Math.log(Math.tan(lat)+1/Math.cos(lat))/Math.PI)/2*scale];
    }
    unproject(point) {
      const scale=256*Math.pow(2,this.zoom),n=Math.PI-2*Math.PI*Math.max(0,Math.min(scale,point[1]))/scale;
      return [Math.max(-85,Math.min(85,180/Math.PI*Math.atan(Math.sinh(n)))),((point[0]/scale*360)%360+360)%360-180];
    }
    fit(records) {
      const points=records.filter(r=>r.point).map(r=>r.point);
      if (!points.length) return;
      if (points.length === 1) {this.center=points[0];this.zoom=records[0]?.exact ? 13 : 10;return;}
      const projected=points.map(p=>this.project(p,0));
      const min=[Math.min(...projected.map(p=>p[0])),Math.min(...projected.map(p=>p[1]))];
      const max=[Math.max(...projected.map(p=>p[0])),Math.max(...projected.map(p=>p[1]))];
      const width=Math.max(200,this.map.clientWidth-110),height=Math.max(120,this.map.clientHeight-110);
      const zoom=Math.floor(Math.log2(Math.min(width/Math.max(.02,max[0]-min[0]),height/Math.max(.02,max[1]-min[1]))));
      this.zoom=Math.max(1,Math.min(12,zoom));
      const scale=Math.pow(2,this.zoom);
      this.center=this.unproject([(min[0]+max[0])/2*scale,(min[1]+max[1])/2*scale]);
    }
    select(record) {
      if (!record?.point) return;
      this.selected=record;
      const index=this.filtered.indexOf(record);
      if(index<0)return;
      this.pageNumber=Math.floor(index/this.pageSize)+1;
      this.renderCards();
      this.center=record.point;
      this.zoom=record.exact ? 14 : 10;
      this.draw();
      const label=record.el.querySelector('.jrl__pin-type');
      label.textContent=record.exact ? this.dataset.exactLabel : this.dataset.areaLabel;
      label.hidden=false;
      record.el.scrollIntoView({block:'nearest',behavior:'instant'});
    }
    draw() {
      if (!this.filtered || !this.isConnected || !this.map.clientWidth) return;
      const width=this.map.clientWidth,height=this.map.clientHeight,center=this.project(this.center),world=256*Math.pow(2,this.zoom),n=Math.pow(2,this.zoom);
      const left=center[0]-width/2,top=center[1]-height/2;
      const alive=new Set();
      for(let x=Math.floor(left/256);x<=Math.floor((left+width)/256);x++) {
        for(let y=Math.max(0,Math.floor(top/256));y<=Math.min(n-1,Math.floor((top+height)/256));y++) {
          const wrapped=((x%n)+n)%n,key=this.zoom+'/'+x+'/'+y;
          alive.add(key);
          let img=this.tileNodes.get(key);
          if(!img){
            img=document.createElement('img');img.alt='';img.width=256;img.height=256;img.draggable=false;
            img.addEventListener('error',()=>{ if (++this.tileFailures>=3){const notice=this.querySelector('.jrl__map-error');notice.textContent=this.dataset.mapError;notice.hidden=false;} },{signal:this.abort.signal});
            const template=this.dataset.tile || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
            const url=template.replace('{z}',String(this.zoom)).replace('{x}',String(wrapped)).replace('{y}',String(y));
            if(url.startsWith('https://'))img.src=url;
            this.tiles.append(img);this.tileNodes.set(key,img);
          }
          img.style.left=(x*256-left)+'px';img.style.top=(y*256-top)+'px';
        }
      }
      this.tileNodes.forEach((img,key)=>{if(!alive.has(key)){img.remove();this.tileNodes.delete(key);}});
      const groups=[];
      this.filtered.filter(r=>r.point).forEach(r=>{
        const p=this.project(r.point),dx=p[0]-center[0];
        const x=width/2+dx-Math.round(dx/world)*world,y=p[1]-top;
        if(x<-40||x>width+40||y<-40||y>height+50)return;
        const group=groups.find(g=>Math.hypot(g.x-x,g.y-y)<42);
        if(group)group.records.push(r);else groups.push({x,y,records:[r]});
      });
      this.pins.replaceChildren();
      groups.forEach(group=>{
        const button=document.createElement('button'),single=group.records.length===1;
        button.type='button';button.className='jrl__pin';button.style.left=group.x+'px';button.style.top=group.y+'px';
        button.textContent=single ? '•' : String(group.records.length);
        button.classList.toggle('is-selected',group.records.includes(this.selected));
        button.setAttribute('aria-label',single ? `${group.records[0].name}, ${group.records[0].city}` : this.dataset.pinLabel.replace('__COUNT__',String(group.records.length)));
        button.title=group.records.map(r=>r.name).join(' · ');
        button.addEventListener('click',()=>{
          if(single){this.select(group.records[0]);return;}
          const sameCity=group.records.every(r=>this.normalize(r.city)===this.normalize(group.records[0].city));
          if(sameCity){this.search.value=group.records[0].city;this.filter();}
          else {this.fit(group.records);this.zoom=Math.min(17,this.zoom+1);this.draw();}
        },{signal:this.abort.signal});
        this.pins.append(button);
      });
    }
  });
}
